import React, { useEffect, useRef, useState } from 'react';
import './RegistrationPage.css';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  AsYouType,
  CountryCode,
  parsePhoneNumberFromString,
  getCountryCallingCode,
} from 'libphonenumber-js';
import { getExampleNumber } from 'libphonenumber-js/core';
import metadata from 'libphonenumber-js/metadata.full.json';
import examples from 'libphonenumber-js/examples.mobile.json';
import { COUNTRY_OPTIONS } from '../utils/dialCodes';
import LoadingOverlay from '../components/LoadingOverlay';
import SmsConsentModal from '../components/SmsConsentModal';

const COUNTRY_MAP = new Map(COUNTRY_OPTIONS.map((c) => [c.code, c]));
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const SESSION_DURATION = 5 * 60; // seconds

enum Stage {
  EnterPhone = 'EnterPhone',
  VerifyOtp = 'VerifyOtp',
}

export default function MfaSetupPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const sessionToken = searchParams.get('session_token');
  const state = searchParams.get('state');

  // Forward to backend /mfa/begin when arriving fresh from Auth0
  useEffect(() => {
    if (sessionToken && state) {
      window.location.href =
        `${BACKEND}/mfa/begin` +
        `?session_token=${encodeURIComponent(sessionToken)}` +
        `&state=${encodeURIComponent(state)}`;
    }
  }, []);

  const [stage, setStage] = useState(Stage.EnterPhone);
  const [sessionCountdown, setSessionCountdown] = useState(SESSION_DURATION);
  const timedOutRef = useRef(false);

  const [phoneDisplay, setPhoneDisplay] = useState('');
  const [selectedCountry, setSelectedCountry] = useState(COUNTRY_OPTIONS[0]);
  const [phoneNumber, setPhoneNumber] = useState('');
  const digitsRef = useRef('');
  const phoneInputRef = useRef<HTMLInputElement>(null);

  const [otp, setOtp] = useState('');
  const [resendCountdown, setResendCountdown] = useState(0);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // ── SMS consent (Twilio A2P 10DLC) ───────────────────────────────────────
  // Never pre-accepted: the modal opens closed-and-unconfirmed, and no SMS is
  // requested until "Agree & Send Code" is clicked.
  const [showSmsConsent, setShowSmsConsent] = useState(false);
  const [smsConsentAccepted, setSmsConsentAccepted] = useState(false);
  const consentSubmittingRef = useRef(false);

  useEffect(() => {
    if (sessionCountdown <= 0) {
      if (!timedOutRef.current) {
        timedOutRef.current = true;
        handleTimeout();
      }
      return;
    }
    const id = setTimeout(() => setSessionCountdown((n) => n - 1), 1000);
    return () => clearTimeout(id);
  }, [sessionCountdown]);

  useEffect(() => {
    if (resendCountdown <= 0) return;
    const id = setTimeout(() => setResendCountdown((n) => n - 1), 1000);
    return () => clearTimeout(id);
  }, [resendCountdown]);

  async function apiPost(path: string, body: object = {}): Promise<any> {
    const res = await fetch(`${BACKEND}/mfa/${path}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const err: any = new Error(data.message || 'Something went wrong.');
      err.status = res.status;
      throw err;
    }
    return data;
  }

  async function handleTimeout() {
    try {
      await fetch(`${BACKEND}/mfa/timeout`, { method: 'POST', credentials: 'include' });
    } catch {}
    navigate('/mfa-expired');
  }

  function formatTime(seconds: number): string {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  }

  function formatNumber(digits: string, countryCode: CountryCode) {
    return new AsYouType(countryCode).input(digits);
  }

  function handlePhoneChange(e: React.ChangeEvent<HTMLInputElement>) {
    const digitsOnly = e.target.value.replace(/\D/g, '');
    const example = getExampleNumber(selectedCountry.code as CountryCode, examples, metadata);
    const maxLength = example?.nationalNumber?.length || 10;
    const limited = digitsOnly.slice(0, maxLength);
    digitsRef.current = limited;
    const formatted = formatNumber(limited, selectedCountry.code as CountryCode);
    setPhoneDisplay(formatted);
    setPhoneNumber(`+${getCountryCallingCode(selectedCountry.code as CountryCode)}${limited}`);
  }

  function handlePhoneKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key !== 'Backspace') return;
    e.preventDefault();
    const newDigits = digitsRef.current.slice(0, -1);
    digitsRef.current = newDigits;
    const formatted = formatNumber(newDigits, selectedCountry.code as CountryCode);
    setPhoneDisplay(formatted);
    setPhoneNumber(`+${getCountryCallingCode(selectedCountry.code as CountryCode)}${newDigits}`);
    setTimeout(() => {
      phoneInputRef.current?.setSelectionRange(formatted.length, formatted.length);
    }, 0);
  }

  // "Send Code" no longer sends: it validates the number and opens the consent
  // modal. The SMS is only requested once the user clicks "Agree & Send Code".
  function handlePhoneSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    const parsed = parsePhoneNumberFromString(phoneNumber);
    if (!parsed?.isValid()) {
      setError('Please enter a valid phone number.');
      return;
    }
    setShowSmsConsent(true);
  }

  async function handleSmsConsentAgree() {
    // Guard against double-clicks: the ref flips synchronously, before React
    // re-renders the disabled button.
    if (consentSubmittingRef.current) return;
    consentSubmittingRef.current = true;

    setSmsConsentAccepted(true);
    setShowSmsConsent(false);
    setError('');
    setLoading(true);
    try {
      // save-phone carries the consent so it is recorded on the MFA session
      // before send-otp — which refuses to send without it — is called.
      await apiPost('save-phone', { phoneNumber, smsConsent: true });
    } catch (err: any) {
      setLoading(false);
      consentSubmittingRef.current = false;
      if (err.status === 401) { navigate('/mfa-expired'); return; }
      setError(err.message);
      return;
    }
    // Advance stage before send-otp so the Resend button is available if the
    // SMS fails — retrying save-phone would hit "Phone already set" since the
    // number was already committed to Auth0 user_metadata above.
    setStage(Stage.VerifyOtp);
    try {
      await apiPost('send-otp');
    } catch (err: any) {
      if (err.status === 401) { navigate('/mfa-expired'); return; }
      setError(err.message);
    } finally {
      setResendCountdown(30);
      setLoading(false);
      consentSubmittingRef.current = false;
    }
  }

  async function handleOtpChange(val: string) {
    const digits = val.replace(/\D/g, '').slice(0, 6);
    setOtp(digits);
    setError('');
    if (digits.length < 6) return;
    setLoading(true);
    try {
      const data = await apiPost('verify-otp', { otp: digits });
      window.location.href = data.continueUrl;
    } catch (err: any) {
      if (err.status === 401) { navigate('/mfa-expired'); return; }
      setError(err.message);
      setOtp('');
    } finally {
      setLoading(false);
    }
  }

  async function handleResend() {
    if (resendCountdown > 0) return;
    // The consent already given covers codes the user explicitly re-requests,
    // so no second modal — but without it there is nothing to resend.
    if (!smsConsentAccepted) return;
    setError('');
    try {
      await apiPost('send-otp');
    } catch (err: any) {
      if (err.status === 401) { navigate('/mfa-expired'); return; }
      setError(err.message);
    } finally {
      setResendCountdown(30);
    }
  }

  // While the /mfa/begin redirect is in flight, render nothing
  if (sessionToken) return null;

  return (
    <div className="registration-page-container">
      <LoadingOverlay show={loading} message="Please wait..." />
      <div className="registration-form-user">
        <div className="mfa-session-timer" style={{ textAlign: 'center', marginBottom: 8, fontSize: 13, color: sessionCountdown < 60 ? '#c0392b' : '#555' }}>
          Session expires in: {formatTime(sessionCountdown)}
        </div>

        {stage === Stage.EnterPhone && (
          <form className="reg-stage-form" onSubmit={handlePhoneSubmit} autoComplete="off">
            <h2>Mobile number</h2>
            <p className="reg-stage-subtext">
              Your organization requires MFA. Enter your mobile number to receive a verification code.
            </p>
            <div className="form-phone-wrapper">
              <select
                className="form-phone-country"
                value={selectedCountry.code}
                onChange={(e) => {
                  const selected = COUNTRY_MAP.get(e.target.value as CountryCode);
                  if (selected) {
                    setSelectedCountry(selected);
                    setPhoneDisplay('');
                    digitsRef.current = '';
                    setPhoneNumber('');
                  }
                }}
              >
                <option key="selected" hidden value={selectedCountry.code}>
                  {selectedCountry.code} {selectedCountry.dial_code}
                </option>
                {COUNTRY_OPTIONS.map((c) => (
                  <option key={c.code} value={c.code}>{c.name} {c.dial_code}</option>
                ))}
              </select>
              <input
                type="tel"
                placeholder="Phone Number"
                inputMode="tel"
                autoComplete="off"
                ref={phoneInputRef}
                value={phoneDisplay}
                onChange={handlePhoneChange}
                onKeyDown={handlePhoneKeyDown}
                onPaste={(e) => e.preventDefault()}
                required
              />
            </div>
            {error && <div className="error-message">{error}</div>}
            <div className="button-group">
              <button type="submit" disabled={loading} className="registrationLoginBtn">
                <span className="invisible-placeholder">Send Code</span>
                <span className="button-content">Send Code</span>
              </button>
            </div>
          </form>
        )}

        {stage === Stage.VerifyOtp && (
          <div className="reg-stage-form">
            <h2>Verify your number</h2>
            <p className="reg-stage-subtext">
              We sent a 6-digit code to <strong>{phoneNumber}</strong>.
            </p>
            <input
              className="reg-otp-input"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="——————"
              maxLength={6}
              value={otp}
              onChange={(e) => handleOtpChange(e.target.value)}
              disabled={loading}
              autoFocus
            />
            {error && <div className="error-message">{error}</div>}
            <div className="reg-resend-text">
              {resendCountdown > 0 ? (
                <span className="reg-resend-countdown">Resend in {resendCountdown}s</span>
              ) : (
                <span className="reg-resend-link" onClick={handleResend}>Resend code</span>
              )}
            </div>
          </div>
        )}
      </div>

      <SmsConsentModal
        show={showSmsConsent}
        onCancel={() => setShowSmsConsent(false)}
        onAgree={handleSmsConsentAgree}
        disabled={loading}
      />
    </div>
  );
}
